import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import {
  IHistoryLtv,
  useGetHistoryLtvsQuery,
  useDeleteLtvMutation,
  useLtvCreatedSubscription,
  useLtvDeletedSubscription,
  useLtvUpdatedSubscription
} from '../../../../generated/graphql';
import { canUserCreate } from '../../../../utils/rightUtils';
import { renderDate } from '../../../../utils/styleUtils';
import { IHeadCell } from '../../../Common/Tables/OverviewTable';
import { Box } from '@mui/material';
import InnerLoader from '../../../Common/InnerLoader';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../../../Common/Fields/EpraDatePicker';
import DenseTable from '../../../Common/Tables/DenseTable';
import LtvForm from './LtvForm';

interface IProps {
  stockId: string | undefined;
  stockName: string | undefined;
}

const LtvTab: React.FunctionComponent<IProps> = ({ stockId, stockName }: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [allData, setAllData] = useState([]);
  const [openLtvForm, setOpenLtvForm] = useState(false);
  const [ltv, setLtv] = useState<IHistoryLtv | null>(null);
  const canCreate = canUserCreate(state.user, Types.Stock);
  const { data, loading } = useGetHistoryLtvsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      stock_id: parseInt(stockId as string)
    }
  });
  const [deleteLtv] = useDeleteLtvMutation();
  const createLtvSubscription = useLtvCreatedSubscription({
    variables: { stock_id: stockId || '' }
  });
  const deleteLtvSubscription = useLtvDeletedSubscription({
    variables: { stock_id: stockId || '' }
  });
  const updateLtvSubscription = useLtvUpdatedSubscription({
    variables: { stock_id: stockId || '' }
  });

  useEffect(() => {
    if (data && data.result) {
      const parsedData = data.result.map((n) => {
        const date = new Date(parseInt(n?.start_date as string));
        date.setHours(12);
        return {
          ...n,
          filter_date: date.toISOString().slice(0, 10)
        };
      }) as never[];
      setAllData(parsedData as never[]);
    }
  }, [data?.result]);

  useEffect(() => {
    if (createLtvSubscription.data?.result) {
      setAllData([createLtvSubscription.data?.result as never, ...allData]);
    }
    if (deleteLtvSubscription.data?.result) {
      removeLtv(deleteLtvSubscription.data.result);
    }
    if (updateLtvSubscription.data?.result) {
      const updated = updateLtvSubscription.data?.result as IHistoryLtv;
      const data = allData as IHistoryLtv[];
      const result = data.map((d) => (d.id === updated.id ? updated : d));
      setAllData(result as never[]);
    }
  }, [createLtvSubscription.data, deleteLtvSubscription.data, updateLtvSubscription.data]);

  const columns = [
    { title: 'Details', sorting: false, width: 82 },
    {
      title: 'Date Day',
      field: 'start_date',
      width: 167,
      type: 'date',
      render: (rowData: IHistoryLtv) => renderDate(rowData.start_date)
    },
    { title: 'LTV value', field: 'value', width: 1321 },
    { title: 'Remove', sorting: false, field: 'status.name', width: 71 }
  ] as IHeadCell[];

  const handleClose = (saved: boolean, isNew?: boolean, ltv?: IHistoryLtv) => {
    if (saved && ltv) {
      if (isNew) {
        setAllData([ltv as never, ...allData]);
      }
    }

    setLtv(null);
    setOpenLtvForm(false);
  };

  const handeDetails = (rowData: IHistoryLtv) => {
    setLtv(rowData);
    setOpenLtvForm(true);
  };

  const removeLtv = (input: IHistoryLtv) => {
    const result = (allData as IHistoryLtv[]).filter((l) => l.id !== input.id);
    setAllData(result as never[]);
  };

  const handleRemove = (rowData: IHistoryLtv) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ltv from ${renderDate(rowData.start_date)}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDelete = (ltv: IHistoryLtv) => {
    dispatch({
      type: Actions.HideConfirmation
    });
    deleteLtv({
      variables: {
        id: ltv.id
      },
      update() {
        removeLtv(ltv);
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'success',
            text: `Ltv from ${renderDate(ltv.start_date)} deleted`
          }
        });
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
  };

  return (
    <Box>
      {loading ? (
        <InnerLoader />
      ) : (
        <Box className="print\:w-full" sx={{ pt: '25px' }}>
          <DenseTable
            columns={columns}
            data={allData}
            handleDetails={handeDetails}
            handleRemove={handleRemove}
            maxHeight={1050}
            pageSize={15}
            createButtonProps={{
              id: 'create-ltv-button',
              onClick: () => setOpenLtvForm(true),
              text: 'Create',
              disabled: !canCreate
            }}
          />
          {openLtvForm && (
            <LtvForm stockId={stockId as string} ltvInput={ltv} handleClose={handleClose} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default LtvTab;
